import { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import { getCroppedImg, Area } from '../../utils/cropImage'

interface ImageCropModalProps {
  imageSrc: string
  onCropSave: (croppedFile: File) => void
  onCancel: () => void
  targetWidth?: number
  targetHeight?: number
  fileName?: string
}

const ImageCropModal = ({
  imageSrc,
  onCropSave,
  onCancel,
  targetWidth = 1920,
  targetHeight = 1080,
  fileName = 'banner.jpg'
}: ImageCropModalProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [cropError, setCropError] = useState<string | null>(null)

  const onCropCompleteCallback = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    []
  )

  const handleSave = async () => {
    if (!croppedAreaPixels) return
    setIsProcessing(true)
    setCropError(null)

    try {
      const croppedFile = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        fileName,
        targetWidth,
        targetHeight
      )
      onCropSave(croppedFile)
    } catch (err) {
      console.error('Failed to crop image:', err)
      setCropError('Failed to crop image. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const aspectRatio = targetWidth / targetHeight

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4">
      <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-3xl w-full border border-gray-700 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <svg className="w-6 h-6 text-primary-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Crop Banner Image
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Adjust crop area to match exact banner requirement ({targetWidth} × {targetHeight} pixels)
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Cropper Container */}
        <div className="relative w-full h-80 sm:h-96 my-4 bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropCompleteCallback}
          />
        </div>

        {/* Error message */}
        {cropError && (
          <div className="mb-3 p-3 bg-red-500/20 border border-red-500 rounded-lg">
            <p className="text-red-400 text-sm">{cropError}</p>
          </div>
        )}

        {/* Controls */}
        <div className="space-y-4 pt-2">
          <div className="flex items-center space-x-4 bg-gray-700/50 p-3 rounded-lg border border-gray-600">
            <span className="text-sm text-gray-300 font-semibold whitespace-nowrap">Zoom:</span>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-label="Zoom"
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-primary-light"
            />
            <span className="text-sm text-gray-400 w-12 text-right">{zoom.toFixed(1)}x</span>
          </div>

          <div className="flex space-x-4 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isProcessing}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 ${
                isProcessing
                  ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                  : 'bg-primary-light hover:bg-primary-dark text-white'
              }`}
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Crop & Save Banner</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageCropModal
